/** A custom field that can be suggested for Wix users to add to member profiles. */
export interface CustomFieldSuggestion {
  /**
   * Custom field name.
   * @maxLength 150
   */
  name?: string | null;
  /** Privacy level of the custom field. */
  defaultPrivacy?: PrivacyWithLiterals;
  /** Type of information to provide for members. */
  fieldType?: TypeWithLiterals;
  /** Social network type. */
  socialType?: SocialTypeTypeWithLiterals;
  /** Field origin. */
  fieldOrigin?: OriginWithLiterals;
  /**
   * Custom field ID.
   * @format GUID
   */
  customFieldId?: string | null;
}

export enum Privacy {
  /** Unknown privacy. This value isn't used. */
  UNKNOWN = 'UNKNOWN',
  /** The information appears on the members' public profile pages. */
  PUBLIC = 'PUBLIC',
  /** Only the member can see this information. */
  PRIVATE = 'PRIVATE',
}

/** @enumType */
export type PrivacyWithLiterals = Privacy | 'UNKNOWN' | 'PUBLIC' | 'PRIVATE';

export enum Type {
  /** Unknown field type. This value isn't used. */
  UNKNOWN = 'UNKNOWN',
  /** A text box to write text. */
  TEXT = 'TEXT',
  /** Only a number can be entered into the field. */
  NUMBER = 'NUMBER',
  /** Only a date can be entered into the field. */
  DATE = 'DATE',
  /** Only a URL can be entered into the field. */
  URL = 'URL',
  /** Only a link from the selected social media platform can be entered into the field. */
  SOCIAL = 'SOCIAL',
}

/** @enumType */
export type TypeWithLiterals =
  | Type
  | 'UNKNOWN'
  | 'TEXT'
  | 'NUMBER'
  | 'DATE'
  | 'URL'
  | 'SOCIAL';

export enum SocialTypeType {
  /** Unknown social type. This value isn't used. */
  UNKNOWN = 'UNKNOWN',
  /** Facebook social media platform. */
  FACEBOOK = 'FACEBOOK',
  /** Instagram social media platform. */
  INSTAGRAM = 'INSTAGRAM',
  /** LinkedIn social media platform. */
  LINKEDIN = 'LINKEDIN',
  /** X (formerly Twitter) social media platform. */
  TWITTER = 'TWITTER',
  /** Youtube social media platform. */
  YOUTUBE = 'YOUTUBE',
  /** Pinterest social media platform. */
  PINTEREST = 'PINTEREST',
  /** TikTok social media platform. */
  TIKTOK = 'TIKTOK',
  /** DeviantArt social media platform. */
  DEVIANTART = 'DEVIANTART',
  /** SoundCloud social media platform. */
  SOUNDCLOUD = 'SOUNDCLOUD',
  /** Tumblr social media platform. */
  TUMBLR = 'TUMBLR',
  /** Vimeo social media platform. */
  VIMEO = 'VIMEO',
  /** VKontakte social media platform. */
  VKONTAKTE = 'VKONTAKTE',
  /** Odnoklassniki social media platform. */
  ODNOKLASSNIKI = 'ODNOKLASSNIKI',
  /** Other social media platform. */
  OTHER = 'OTHER',
}

/** @enumType */
export type SocialTypeTypeWithLiterals =
  | SocialTypeType
  | 'UNKNOWN'
  | 'FACEBOOK'
  | 'INSTAGRAM'
  | 'LINKEDIN'
  | 'TWITTER'
  | 'YOUTUBE'
  | 'PINTEREST'
  | 'TIKTOK'
  | 'DEVIANTART'
  | 'SOUNDCLOUD'
  | 'TUMBLR'
  | 'VIMEO'
  | 'VKONTAKTE'
  | 'ODNOKLASSNIKI'
  | 'OTHER';

export enum Origin {
  /** Unknown field origin. This value isn't used. */
  UNKNOWN = 'UNKNOWN',
  /** Custom field created by a Wix user. */
  CUSTOM = 'CUSTOM',
  /** Contact type of field. These are default fields that are already provided by Members Area, such as: "birth date", "position", "company". */
  CONTACT = 'CONTACT',
  /** System fields, such as: "first name", "last name", "email", "phone", "address", and  "title". Some system fields are only available from the dashboard. */
  SYSTEM = 'SYSTEM',
}

/** @enumType */
export type OriginWithLiterals =
  | Origin
  | 'UNKNOWN'
  | 'CUSTOM'
  | 'CONTACT'
  | 'SYSTEM';

export interface ListCustomFieldSuggestionsRequest {
  paging?: Paging;
  sorting?: Sorting[];
}

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface ListCustomFieldSuggestionsResponse {
  /** Custom field suggestions. */
  suggestions?: CustomFieldSuggestion[];
  /** Metadata for the paginated results. */
  metadata?: PagingMetadata;
}

export interface PagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
}

export interface QueryCustomFieldSuggestionsRequest {
  /** Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language) for more details. */
  query?: Query;
}

export interface Query {
  /**
   * Filter object.
   *
   * Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).
   */
  filter?: any;
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  paging?: Paging;
  /**
   * Sort object.
   *
   * Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section).
   */
  sorting?: Sorting[];
}

export interface QueryCustomFieldSuggestionsResponse {
  /** Custom field suggestion. */
  suggestions?: CustomFieldSuggestion[];
  /** Metadata for the paginated results. */
  metadata?: PagingMetadata;
}
